import { type ReactNode } from "react"

import RitualWave from "@/assets/svg/aura-scan/ritual-wave-top.svg?react"
import { cn } from "@/lib/utils"

import {
  ADVISORS_COPY,
  ARCHETYPE_AFFIRMATION,
  SENSITIVITY_LABELS,
  WRAPPING_SUMMARY,
} from "../config/copy"
import { artFor } from "../config/arts"
import { planetIconFor, titleCase, zodiacIconFor } from "../config/icons"
import type { AuraScanReport, AuraColor, AuraTrait } from "../types"
import { DesignCanvas } from "./design-canvas"
import { SensitivityGauge } from "./sensitivity-gauge"

const TAG_TONES = [
  "bg-peach/20 text-peach",
  "bg-gold/20 text-gold",
  "bg-cyan/20 text-cyan",
] as const

function formatBorn(birthDate: string, birthTime: string | null): string {
  const [year, month, day] = birthDate.split("-").map(Number)
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1))
  const dayNum = date.getUTCDate()
  const monthName = date.toLocaleString("en-GB", { month: "long", timeZone: "UTC" })
  const time = birthTime ? `, ${birthTime}` : ""
  return `Born ${dayNum} ${monthName} ${year}${time}`
}

export function AuraScanReportView({
  content,
  className,
}: {
  content: AuraScanReport
  className?: string
}) {
  const art = artFor(content.archetype.id)
  const SunIcon = zodiacIconFor[content.signs.sunSign]
  const MoonIcon = zodiacIconFor[content.signs.moonSign]
  const PlanetIcon = planetIconFor[content.signs.rulingPlanet]
  const moonApprox = content.signs.moonPrecision === "estimated"

  return (
    <div className={cn("bg-dark-base text-purple-light", className)}>
      <DesignCanvas>
        <section>
          <img src={art.hero} alt="Aura Scan Report" className="block w-[1200px]" />
        </section>

        <section className="px-[120px] py-20">
          <div className="flex flex-col items-center gap-6 overflow-hidden rounded-[48px] bg-dark-surface p-[50px]">
            <div className="flex w-full flex-col gap-2 text-center">
              <p className="font-body text-2xl font-medium leading-8 text-main-purple">
                Your aura archetype is
              </p>
              <h2 className="font-display text-[64px] font-semibold leading-[88px] text-purple-light">
                {content.archetype.name}
              </h2>
              <p className="font-body text-2xl font-medium leading-8 text-purple-light/70">
                {formatBorn(content.subject.birthDate, content.subject.birthTime)}
              </p>
            </div>
            <div className="flex w-full flex-col items-center gap-8">
              <img
                src={art.overview}
                alt=""
                className="h-[537px] w-[960px] object-cover mix-blend-lighten"
              />
              <div className="flex flex-wrap items-center justify-center gap-2">
                <SignTag
                  icon={<SunIcon className="size-8" />}
                  label={`${titleCase(content.signs.sunSign)} sun`}
                />
                <SignTag
                  icon={<MoonIcon className="size-8" />}
                  label={`${moonApprox ? "≈ " : ""}${titleCase(content.signs.moonSign)} moon`}
                />
                <SignTag
                  icon={<PlanetIcon className="size-8" />}
                  label={`Ruled by the ${titleCase(content.signs.rulingPlanet)}`}
                />
              </div>
              <p className="w-full font-body text-[32px] leading-[48px] text-purple-light/70">
                {content.archetype.oneLiner}
              </p>
            </div>
          </div>
        </section>

        <section>
          <div className="relative">
            <img src={art.signature} alt="" className="h-[670px] w-[1200px] object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-dark-base to-transparent px-[120px] pb-2 pt-24">
              <div className="flex flex-wrap justify-center gap-2">
                {content.signature.tags.map((tag, i) => (
                  <span
                    key={tag}
                    className={cn(
                      "rounded-full px-6 py-2 font-body text-2xl font-medium",
                      TAG_TONES[i % TAG_TONES.length],
                    )}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="mt-6 text-center font-display text-[64px] font-semibold leading-[88px] text-purple-light">
                Your Aura Signature
              </h2>
            </div>
          </div>
          <div className="flex flex-col gap-10 px-[120px] pb-20 pt-10">
            <ColorRow
              icon={art.colorPrimary}
              caption="Primary aura color"
              color={content.signature.primaryColor}
            />
            <div className="h-px bg-purple-light/20" />
            <ColorRow
              icon={art.colorSecondary}
              caption="Secondary aura color"
              color={content.signature.secondaryColor}
            />
            <div className="h-px bg-purple-light/20" />
            <div>
              <h3 className="font-display text-[36px] font-medium leading-[52px] text-purple-light">
                What this means
              </h3>
              <p className="mt-4 font-body text-[32px] leading-[48px] text-purple-light/70">
                {content.signature.description}
              </p>
            </div>
          </div>
        </section>

        <section>
          <div className="relative">
            <img src={art.sensitivity} alt="" className="h-[670px] w-[1200px] object-cover" />
            <h2 className="absolute inset-x-0 bottom-8 px-[120px] text-center font-display text-[64px] font-semibold leading-[88px] text-purple-light">
              Sensitivity Profile
            </h2>
          </div>
          <div className="flex flex-col px-[120px] pb-20 pt-10">
            <SensitivityRow
              title={SENSITIVITY_LABELS.natureAttunement}
              scale={content.sensitivity.natureAttunement}
            />
            <div className="h-px bg-purple-light/20" />
            <SensitivityRow
              title={SENSITIVITY_LABELS.energySensitivity}
              scale={content.sensitivity.energySensitivity}
            />
            <div className="h-px bg-purple-light/20" />
            <SensitivityRow
              title={SENSITIVITY_LABELS.intuitiveReception}
              scale={content.sensitivity.intuitiveReception}
            />
          </div>
        </section>

        <div className="flex flex-col gap-20 px-[120px] py-20">
          <TraitCard title="Your Superpowers" hero={art.superpowers} traits={content.superpowers} />
          <TraitCard title="Your Growth Edges" hero={art.growth} traits={content.growthEdges} />
          <TraitCard title="Your Power Channels" hero={art.channels} traits={content.powerChannels} />
        </div>

        <section className="relative w-[1200px] bg-dark-base">
          <div className="relative flex flex-col items-center bg-dark-surface">
            <div className="relative mb-[-128px] h-[863px] w-full isolate">
              <div className="absolute inset-x-0 top-0 h-[100px] bg-dark-base" />
              <div className="absolute inset-x-0 bottom-0 top-[100px] bg-dark-surface" />
              <img
                src={art.ritual}
                alt=""
                className="absolute inset-x-0 top-[100px] h-[763px] w-full object-cover object-bottom mix-blend-lighten"
              />
              <RitualWave className="absolute inset-x-0 top-0 z-[1] h-[100px] w-full rotate-180 text-dark-surface" />
            </div>
            <div className="relative z-10 flex w-[960px] flex-col gap-10 pb-20">
              <h2 className="text-center font-display text-[64px] font-semibold leading-[88px] text-purple-light">
                Your Daily Ritual
              </h2>
              <div className="flex w-full flex-col gap-12">
                <div className="flex w-full flex-col gap-7">
                  <div className="flex w-full flex-col gap-2 text-center">
                    <h3 className="font-display text-[36px] font-medium leading-[52px] text-purple-light">
                      {content.ritual.title}
                    </h3>
                    <p className="font-body text-2xl font-medium leading-8 text-main-purple">
                      {content.ritual.when}
                    </p>
                  </div>
                  <p className="font-body text-[32px] leading-[48px] text-purple-light/70">
                    {content.ritual.intro}
                  </p>
                </div>
                <ol className="flex w-full flex-col gap-10">
                  {content.ritual.steps.map((step, i) => (
                    <li key={i} className="flex w-full items-stretch gap-6">
                      <span className="flex w-20 shrink-0 items-center justify-center rounded-full border border-purple-light/10 bg-gradient-to-b from-purple-light/20 to-purple-light/5 px-6 py-3 font-display text-[36px] font-medium leading-[52px] text-purple-light backdrop-blur-[8px]">
                        {i + 1}
                      </span>
                      <div className="flex flex-1 items-center pb-3">
                        <p className="font-body text-[32px] leading-[48px] text-purple-light/70">
                          {step}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
          <div className="flex h-[100px] w-[1200px] items-center justify-center bg-dark-base">
            <div className="h-[100px] w-[1200px] rotate-180 -scale-y-100">
              <RitualWave className="h-full w-full text-dark-surface" />
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-8 px-[120px] py-20">
          <h2 className="text-center font-display text-[64px] font-semibold leading-[88px] text-purple-light">
            {ADVISORS_COPY.title}
          </h2>
          <p className="font-body text-[32px] leading-[48px] text-purple-light/70">
            {ADVISORS_COPY.body}
          </p>
          <p className="font-display text-[36px] font-medium leading-[52px] text-purple-light">
            {ADVISORS_COPY.closer}
          </p>
          <img src={art.advisors} alt="" className="w-full" />
        </section>

        <section className="relative min-h-[1600px] overflow-hidden px-[120px] pt-8">
          <img
            src={art.wrapping}
            alt=""
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[1362px] w-full object-cover object-bottom mix-blend-lighten"
          />
          <h2 className="relative text-center font-display text-[64px] font-semibold leading-[88px] text-purple-light">
            Wrapping Up
          </h2>
          <div className="relative mt-8 flex flex-col gap-8 text-center">
            {WRAPPING_SUMMARY.map((p) => (
              <p
                key={p.slice(0, 24)}
                className="font-body text-[32px] leading-[48px] text-purple-light/70"
              >
                {p}
              </p>
            ))}
            <p className="font-display text-[36px] font-medium leading-[52px] text-[#ddad63]">
              {ARCHETYPE_AFFIRMATION[content.archetype.id]}
            </p>
          </div>
        </section>
      </DesignCanvas>
    </div>
  )
}

function SignTag({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-[23px] bg-purple-light/10 px-4 py-2 font-body text-2xl leading-8 text-purple-light/70">
      {icon}
      {label}
    </span>
  )
}

function ColorRow({
  icon,
  caption,
  color,
}: {
  icon: string
  caption: string
  color: AuraColor
}) {
  return (
    <div className="flex gap-8">
      <img src={icon} alt="" className="h-[138px] w-[122px] shrink-0 object-contain" />
      <div className="min-w-0">
        <p className="font-body text-2xl text-main-purple">{caption}</p>
        <p className="font-display text-[44px] font-medium leading-tight text-purple-light">
          {color.name}
        </p>
        <p className="mt-4 font-body text-[32px] leading-[48px] text-purple-light/70">
          {color.description}
        </p>
      </div>
    </div>
  )
}

function SensitivityRow({
  title,
  scale,
}: {
  title: string
  scale: AuraScanReport["sensitivity"]["natureAttunement"]
}) {
  return (
    <div className="flex items-center justify-between gap-10 py-10">
      <div className="w-[636px] shrink-0">
        <h3 className="font-display text-[36px] font-medium leading-[52px] text-purple-light">
          {title}
        </h3>
        <p className="mt-4 font-body text-[32px] leading-[48px] text-purple-light/70">
          {scale.description}
        </p>
      </div>
      <SensitivityGauge score={scale.score} level={scale.level} />
    </div>
  )
}

function TraitCard({
  title,
  hero,
  traits,
}: {
  title: string
  hero: string
  traits: AuraTrait[]
}) {
  return (
    <section className="overflow-hidden rounded-[48px] bg-dark-surface">
      <img src={hero} alt="" className="relative z-10 -mb-16 h-[537px] w-full object-cover" />
      <div className="flex flex-col gap-10 p-[50px] pt-0">
        <h2 className="relative z-20 text-center font-display text-[64px] font-semibold leading-[88px] text-purple-light">
          {title}
        </h2>
        {traits.map((trait) => (
          <div key={trait.title}>
            <h3 className="font-display text-[36px] font-medium leading-[52px] text-purple-light">
              {trait.title}
            </h3>
            <p className="mt-2 font-body text-[32px] leading-[48px] text-purple-light/70">
              {trait.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}
